import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "seals_token";
const LAST_ACTIVITY_KEY = "seals_last_activity";
const INACTIVITY_TIMEOUT_MS = 3 * 60 * 60 * 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth

  const clearSession = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setUser(false);
  };

  const markActivity = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  };

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      setUser(false);
      return;
    }

    const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    if (
      Number.isFinite(lastActivity) &&
      lastActivity > 0 &&
      Date.now() - lastActivity >= INACTIVITY_TIMEOUT_MS
    ) {
      clearSession();
      return;
    }

    if (!lastActivity) markActivity();

    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {
      clearSession();
    });
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    let lastWrite = 0;

    const recordActivity = () => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
      lastWrite = now;
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    };

    const checkInactivity = () => {
      const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
      if (
        !Number.isFinite(lastActivity) ||
        lastActivity <= 0 ||
        Date.now() - lastActivity >= INACTIVITY_TIMEOUT_MS
      ) {
        clearSession();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") checkInactivity();
    };

    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((event) =>
      window.addEventListener(event, recordActivity, { passive: true })
    );
    window.addEventListener("focus", checkInactivity);
    document.addEventListener("visibilitychange", handleVisibility);

    const interval = window.setInterval(checkInactivity, 60 * 1000);

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, recordActivity)
      );
      window.removeEventListener("focus", checkInactivity);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.clearInterval(interval);
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    markActivity();
    setUser(data.user);
  };

  const logout = () => {
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
